import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "../../components/icons";
import { cx } from "../../components/ui";
import { formatBytes } from "../../lib/format";
import {
  ATTACHMENT_ACCEPT,
  MAX_ATTACHMENTS_PER_MESSAGE,
  attachmentProblem,
  withEffectiveType,
} from "../../lib/support";

/**
 * Picking files for a message: a drop zone, a button, and the list of what is
 * queued.
 *
 * It holds no upload logic at all. Both callers post their message first and
 * upload afterwards — the API takes attachments against a message that already
 * exists — so this component's whole job is to end up holding a valid array of
 * files, and to refuse a bad one early enough that nobody watches a 40 MB video
 * upload before being told no.
 */
export function AttachmentPicker({
  files,
  onChange,
  disabled,
}: {
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation("support");
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const accept = (incoming: FileList | null) => {
    if (!incoming || incoming.length === 0) return;

    const next = [...files];
    let refusal: string | null = null;

    for (const file of Array.from(incoming)) {
      const reason = attachmentProblem(file, next.length);
      if (reason) {
        // The first refusal is the one reported. A list of five reasons for
        // five files is a wall of text about a mistake somebody already knows
        // they made.
        refusal ??= t(`attachment.${reason.key}`, reason.values);
        continue;
      }
      next.push(withEffectiveType(file));
    }

    setProblem(refusal);
    onChange(next);
    // Clearing the input matters: picking the same file twice in a row is
    // otherwise a no-op, because the value has not changed.
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = (index: number) => {
    setProblem(null);
    onChange(files.filter((_, position) => position !== index));
  };

  const full = files.length >= MAX_ATTACHMENTS_PER_MESSAGE;

  return (
    <div className="space-y-2">
      <div
        onDragOver={(event) => {
          if (disabled || full) return;
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          if (disabled || full) return;
          event.preventDefault();
          setDragging(false);
          accept(event.dataTransfer.files);
        }}
        className={cx(
          "flex items-center justify-between gap-3 rounded-md border border-dashed px-3 py-2.5 text-[13px] transition-colors duration-200 ease-smooth",
          dragging
            ? "border-accent bg-accent/8 text-accent"
            : "border-line text-fg-subtle hover:border-line-strong",
          (disabled || full) && "opacity-60",
        )}
      >
        <span className="flex min-w-0 items-center gap-2">
          <Icon name="paperclip" className="size-3.5 shrink-0" />
          {/* `min-w-0` as well as `truncate`: a nowrap string is a flex item's
              automatic minimum size, so without it this line sets a floor wider
              than a phone and the whole page scrolls sideways. */}
          <span className="min-w-0 truncate">
            {full
              ? t("attach.limit", { count: MAX_ATTACHMENTS_PER_MESSAGE })
              : t("attach.drop")}
          </span>
        </span>

        <button
          type="button"
          disabled={disabled || full}
          onClick={() => inputRef.current?.click()}
          className="shrink-0 border border-line bg-raised px-2 py-1 text-[12px] text-fg-muted transition-colors duration-200 ease-smooth hover:border-line-strong hover:text-fg disabled:opacity-50 pointer-coarse:min-h-11"
        >
          {t("attach.choose")}
        </button>

        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ATTACHMENT_ACCEPT}
          className="hidden"
          onChange={(event) => accept(event.target.files)}
        />
      </div>

      {problem && <p className="text-[13px] text-danger">{problem}</p>}

      {files.length > 0 && (
        <ul className="space-y-1.5">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${index}`}
              className="flex items-center gap-2 border border-line bg-surface px-2.5 py-1.5 text-[12px]"
            >
              <Icon name="paperclip" className="size-3.5 shrink-0 text-fg-subtle" />
              <span className="min-w-0 flex-1 truncate text-fg-muted">{file.name}</span>
              <span className="shrink-0 tabular-nums text-fg-subtle">
                {formatBytes(file.size)}
              </span>
              <button
                type="button"
                onClick={() => remove(index)}
                disabled={disabled}
                aria-label={t("attach.remove", { name: file.name })}
                className="shrink-0 text-fg-subtle transition-colors hover:text-danger disabled:opacity-50"
              >
                <Icon name="close" className="size-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
